import {
  CreateFieldValueModel,
  EditFieldValueModel,
} from 'shared/domain/fieldValue/fieldValueModel';
import { fieldValuesClient } from 'shared/domain/fieldValue/fieldValueClients';
import { EditableFieldValuesEntityName } from 'shared/domain/fieldValue/fields';
import { FieldValueTranslationModel } from 'shared/domain/fieldValueTranslation/fieldValueTranslationModel';
import {
  CreateFieldValueOnView,
  EditFieldValueOnView,
  FieldValueOnView,
} from 'presentation/fieldValue/fieldValueOnView';

/** Saves the new values and the changes of one field; an empty batch is skipped. */
export async function saveFieldValues(
  fieldName: EditableFieldValuesEntityName,
  newFieldValues: CreateFieldValueModel[],
  fieldValueChanges: EditFieldValueModel[]
): Promise<void> {
  await Promise.all([
    newFieldValues.length ? fieldValuesClient.create(fieldName, newFieldValues) : undefined,
    fieldValueChanges.length ? fieldValuesClient.edit(fieldName, fieldValueChanges) : undefined,
  ]);
}

/**
 * Immutably updates `processes` for a draft field value matched by `localId`.
 * Returns `undefined` when no matching draft exists, so callers can fall back
 * to the persisted-value (`fieldChanges`) path.
 */
export function resolveNewFieldsAfterProcessesUpdate(
  newFields: CreateFieldValueOnView[],
  localId: string,
  processes: string[]
): CreateFieldValueOnView[] | undefined {
  const index = newFields.findIndex((field) => field.localId === localId);
  if (index === -1) {
    return undefined;
  }

  return newFields.map((field, fieldIndex) =>
    fieldIndex === index ? { ...field, processes } : field
  );
}

export function parseChanges(
  allItems: FieldValueOnView[],
  fieldChanges: EditFieldValueOnView[]
): EditFieldValueModel[] {
  return fieldChanges.map((fieldChange) => {
    if (!fieldChange.valueTranslations) {
      return {
        processes: fieldChange.processes,
        _id: fieldChange._id,
      };
    }

    const originalField = allItems.find((item) => {
      return item._id === fieldChange._id;
    })!;

    const newTranslations = fieldChange.valueTranslations.map(
      (possibleChange) => {
        const originalTranslation = originalField.valueTranslations.find(
          (translation) => {
            return possibleChange.localeCode === translation.localeCode;
          }
        );

        if (!originalTranslation) {
          return possibleChange;
        } else if (
          originalTranslation &&
          originalTranslation.value !== possibleChange.value
        ) {
          return possibleChange;
        }

        return undefined;
      }
    );

    return {
      valueTranslations: newTranslations.filter(
        (x) => x
      ) as FieldValueTranslationModel[],
      processes: fieldChange.processes,
      _id: fieldChange._id,
    };
  });
}
